import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { navigate } from 'vike/client/router';
import { useQueryClient } from '@tanstack/react-query';
import type { ApplicationState } from '../../../../../types/tracked-job';
import type { UserStateMap } from '../../../../../types/user-state';
import { extractJobsFromUserState } from '../../../../../utils/user-state/extract-jobs-from-user-state';
import { userStateQueryKey } from '../../../../../query/user-state/userStateQueryKeys';
import { useSavedJobActions } from '../../../../../hooks/useSavedJobActions';
import { useFollowedCompanies } from '../../../../../hooks/useFollowedCompanies';
import { useCompanyFollowActions } from '../../../../../hooks/useCompanyFollowActions';
import { useUserJobStateSync } from '../../../../../hooks/useUserJobStateSync';
import { trackJobApply, trackJobSave, trackJobShare, trackJobUnsave, trackEvent } from '../../../../../utils/analytics/analytics';
import { APPLICATION_STATES } from '../../../../../components/application-state';
import type { DisplayJob } from '../../../../../types';
import type { ToastAction } from '../../../../../contexts/ToastContext';
import { normalizeSupportedLocale } from '../../../../../utils/routing/locale-from-pathname';
import { localizeRoute } from '../../../../../utils/constants/url-constants';
import { maybeShowSaveFollowOfferToast } from '../../../../../utils/job-tracking/save-follow-offer';
import { useFormatMessage } from '../../../../../i18n-review/client/Msg';

export interface JobActions {
  handleToggleSaved: () => Promise<void>;
  handleToggleApplied: () => Promise<void>;
  handleToggleHide: () => void;
  handleSetApplicationState: (state: ApplicationState) => Promise<void>;
  handleShare: () => Promise<void>;
}

export interface UseJobActionsProps {
  displayJob: DisplayJob | null | undefined;
  jobId: string | undefined;
  isSaved: boolean;
  isApplied: boolean;
  isHidden: boolean;
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number, action?: ToastAction) => void;
  url?: string;
}

/**
 * Hook to manage all actions for a job detail page
 * Handles save, unsave, apply, hide, share, and application state changes
 */
export function useJobActions({
  displayJob,
  jobId,
  isSaved,
  isApplied,
  isHidden,
  showToast,
  url,
}: UseJobActionsProps): JobActions {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const queryClient = useQueryClient();
  const { saveJob, unsaveJob } = useSavedJobActions();
  const followedCompanies = useFollowedCompanies();
  const { followCompany } = useCompanyFollowActions();
  const { setAppliedJobState, syncApplicationStateToBackend, syncJobIsHiddenToBackend } =
    useUserJobStateSync();

  const handleToggleSaved = useCallback(async () => {
    if (!displayJob) return;
    const payload = { ...displayJob, id: displayJob.objectID };

    if (!isSaved) {
      trackJobSave({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        locale: intl.locale,
      });
      const success = await saveJob(payload);
      if (success) {
        const locale = normalizeSupportedLocale(intl.locale);
        const liveUserState = queryClient.getQueryData<UserStateMap>(userStateQueryKey);
        const savedCount = extractJobsFromUserState(liveUserState ?? {}).savedJobs.length;
        const offered = maybeShowSaveFollowOfferToast({ intl, formatMessage, showToast, displayJob, followedCompanies, followCompany });
        if (!offered) {
          showToast(
            formatMessage(
              {
                id: 'hit.toast.savedWithCount',
                defaultMessage: 'Saved — {count, plural, one {# job} other {# jobs}} in My Jobs',
                description: 'Success toast shown when a job is saved, with the running count of saved jobs',
              },
              { count: savedCount }
            ),
            'success',
            3000,
            {
              label: formatMessage({ id: 'hit.toast.viewMyJobs', defaultMessage: 'View →', description: 'Toast action link into My Jobs after saving a job' }),
              onClick: () => navigate(`/${locale}${localizeRoute('/tools', locale)}`),
            }
          );
        }
      }
    } else {
      trackJobUnsave({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        locale: intl.locale,
      });
      const success = await unsaveJob(payload);
      if (success) {
        showToast(
          formatMessage({
            id: 'hit.toast.unsaved',
            defaultMessage: 'Job removed from saved',
            description: 'Info toast notification message shown when a job is removed from saved jobs',
          }),
          'info'
        );
      }
    }
  }, [displayJob, isSaved, saveJob, unsaveJob, followedCompanies, followCompany, intl, formatMessage, showToast, queryClient]);

  const handleToggleApplied = useCallback(async () => {
    if (!displayJob) return;
    const willBeApplied = !isApplied;
    const payload = { ...displayJob, id: displayJob.objectID };
    const success = await setAppliedJobState(payload, willBeApplied);
    if (success) {
      if (willBeApplied) {
        trackJobApply({
          jobId: displayJob.objectID,
          jobTitle: displayJob.normalisedTitle || displayJob.title || '',
          companyId: displayJob.companyId || '',
          source: 'tracker_marked',
          locale: intl.locale,
        });
        showToast(
          formatMessage({
            id: 'hit.toast.applied',
            defaultMessage: 'Marked as applied',
            description: 'Success toast notification message shown when a job is marked as applied to',
          }),
          'success'
        );
      } else {
        showToast(
          formatMessage({
            id: 'hit.toast.unapplied',
            defaultMessage: 'Removed from applied',
            description: 'Info toast notification message shown when applied status is removed from a job',
          }),
          'info'
        );
      }
    }
  }, [isApplied, setAppliedJobState, displayJob, intl, formatMessage, showToast]);

  const handleToggleHide = useCallback(() => {
    if (!jobId) return;
    const willBeHidden = !isHidden;
    // syncJobIsHiddenToBackend's own upsert is non-fatal on failure (see its doc comment in
    // useUserJobStateSync.ts) — the toast reflects the optimistic intent, same as before.
    void syncJobIsHiddenToBackend(jobId, willBeHidden);

    trackEvent({
      name: 'job_visibility_change',
      params: {
        job_id: jobId,
        action: willBeHidden ? 'hide' : 'unhide',
        company_id: displayJob?.companyId || undefined,
        locale: intl.locale,
      },
    });

    showToast(
      willBeHidden
        ? formatMessage({
            id: 'hit.toast.hidden',
            defaultMessage: 'Job hidden',
            description: 'Info toast notification message shown when a job is hidden',
          })
        : formatMessage({
            id: 'hit.toast.unhidden',
            defaultMessage: 'Job unhidden',
            description: 'Success toast notification message shown when a previously hidden job is unhidden',
          }),
      willBeHidden ? 'info' : 'success'
    );
  }, [isHidden, jobId, intl, formatMessage, showToast, syncJobIsHiddenToBackend, displayJob]);

  const handleSetApplicationState = useCallback(async (state: ApplicationState) => {
    if (!displayJob || !jobId) return;

    const payload = { ...displayJob, id: displayJob.objectID };
    const success = await syncApplicationStateToBackend(jobId, state, payload);

    if (success) {
      if (state !== null && !isApplied) {
        trackJobApply({
          jobId: displayJob.objectID,
          jobTitle: displayJob.normalisedTitle || displayJob.title || '',
          companyId: displayJob.companyId || '',
          source: 'tracker_marked',
          locale: intl.locale,
        });
      }

      const stateConfig = APPLICATION_STATES.find((s) => s.value === state);
      if (stateConfig) {
        showToast(
          formatMessage(
            {
              id: 'tools.applicationState.updated',
              defaultMessage: 'State updated to {state}',
              description: 'Success toast message when application state is changed, where {state} is the new state name',
            },
            { state: formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }) }
          ),
          'success'
        );
      }
    }
  }, [jobId, intl, formatMessage, showToast, isApplied, syncApplicationStateToBackend, displayJob]);

  const handleShare = useCallback(async () => {
    if (!displayJob) return;
    const { sharePage } = await import('../utils/shareUtils');
    const currentUrl = typeof window !== 'undefined' ? window.location.href : url || '';
    const jobTitle = displayJob.title || displayJob.normalisedTitle || document.title;

    trackJobShare({
      jobId: displayJob.objectID,
      jobTitle: displayJob.normalisedTitle || displayJob.title || '',
      locale: intl.locale,
    });

    await sharePage(
      currentUrl,
      jobTitle,
      () => {
        showToast(
          formatMessage({
            id: 'hit.linkCopied',
            defaultMessage: 'Link copied to clipboard!',
            description: 'Message shown when link is copied to clipboard',
          }),
          'success'
        );
      },
      (error) => {
        console.error('Share failed:', error);
        showToast(
          formatMessage({
            id: 'hit.shareError',
            defaultMessage: 'Something went wrong while trying to share this job.',
            description: "Message shown when there's an error sharing",
          }),
          'error'
        );
      }
    );
  }, [displayJob, url, intl, formatMessage, showToast]);

  return {
    handleToggleSaved,
    handleToggleApplied,
    handleToggleHide,
    handleSetApplicationState,
    handleShare,
  };
}
